// src/components/pages/TiffinService.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getColors } from '../../styles/colors';
import { 
  FaClock, FaFire, FaUtensils, FaLeaf, FaTruck, 
  FaCalendarCheck, FaArrowRight, FaTimes,
  FaCalendarAlt, FaCheck, FaPlus, FaMinus,
  FaCalendarWeek, FaBookmark, FaInfoCircle,
  FaSpinner, FaFish, FaExclamationTriangle,
  FaTrash, FaList, FaCalendarDay, FaSave, FaEraser,
  FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { 
  createSubscriptionAsync, 
  getSubscriptionListAsync, 
  cancelSubscriptionAsync 
} from '../../redux/slices/subscriptionSlice';
import { customDateApi } from '../../api/customDateApi';
import apiService from '../../api/authApi';

const TiffinService = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { isLoading, isSubscribing, error: subscriptionError } = useSelector((state) => state.subscription || {});
  
  // State for view mode
  const [viewMode, setViewMode] = useState('monthly');
  const [showModal, setShowModal] = useState(false);
  
  // Weekly Menu State
  const [weeklyMenu, setWeeklyMenu] = useState([]);
  const [weeklyMenuLoading, setWeeklyMenuLoading] = useState(false);
  const [weeklyMenuError, setWeeklyMenuError] = useState(null);
  
  // Monthly Subscription State
  const [subscriptionStartDate, setSubscriptionStartDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [duration, setDuration] = useState(30);
  const [mealItems, setMealItems] = useState([
    { 
      meal_time: 'Breakfast', 
      time_slot: '07:30 AM - 10:00 AM',
      veg: { selected: true, quantity: 1 }, 
      nonVeg: { selected: true, quantity: 1 }  // ✅ Non-Veg selected by default
    },
    { 
      meal_time: 'Lunch', 
      time_slot: '12:00 PM - 02:30 PM',
      veg: { selected: true, quantity: 1 }, 
      nonVeg: { selected: true, quantity: 1 }  // ✅ Non-Veg selected by default
    },
    { 
      meal_time: 'Dinner', 
      time_slot: '07:30 PM - 10:00 PM',
      veg: { selected: true, quantity: 1 }, 
      nonVeg: { selected: true, quantity: 1 }  // ✅ Non-Veg selected by default
    }
  ]);
  
  // Custom Date State
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [customBookingEntries, setCustomBookingEntries] = useState([]);
  const [showMealPicker, setShowMealPicker] = useState(false);
  
  // Meal picker state
  const [pickerMealTime, setPickerMealTime] = useState('Breakfast');
  const [pickerMealType, setPickerMealType] = useState('veg');
  const [pickerQuantity, setPickerQuantity] = useState(1);
  const [pickerDate, setPickerDate] = useState(null);
  
  // Local error state
  const [localError, setLocalError] = useState(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  // Meal prices
  const mealPrices = { Breakfast: 50, Lunch: 70, Dinner: 90 };
  const durationOptions = [7, 15, 30, 60];

  // ========== EFFECTS ==========
  useEffect(() => {
    checkExistingSubscription();
  }, []);

  // ========== FETCH WEEKLY MENU ==========
  const fetchWeeklyMenu = async () => {
    setWeeklyMenuLoading(true);
    setWeeklyMenuError(null);
    
    try {
      console.log('🔄 Fetching weekly menu from API...');
      const response = await apiService.getWeeklyMenu();
      console.log('✅ Weekly menu response:', response);
      
      let menuItems = [];
      
      if (response?.data && Array.isArray(response.data)) {
        menuItems = response.data;
      } else if (response?.grouped) {
        const grouped = response.grouped;
        Object.keys(grouped).forEach(day => {
          if (Array.isArray(grouped[day])) {
            grouped[day].forEach(item => {
              menuItems.push({
                ...item,
                day: day
              });
            });
          }
        });
      } else if (response?.data && typeof response.data === 'object') {
        const grouped = response.data;
        Object.keys(grouped).forEach(day => {
          if (Array.isArray(grouped[day])) {
            grouped[day].forEach(item => {
              menuItems.push({
                ...item,
                day: day
              });
            });
          }
        });
      } else if (Array.isArray(response)) {
        menuItems = response;
      }
      
      setWeeklyMenu(menuItems);
      console.log(`✅ Loaded ${menuItems.length} weekly menu items`);
    } catch (error) {
      console.error('❌ Failed to fetch weekly menu:', error);
      setWeeklyMenuError(error.message || 'Failed to load menu');
      setWeeklyMenu([]);
    } finally {
      setWeeklyMenuLoading(false);
    }
  };

  // ========== SUBSCRIPTION FUNCTIONS ==========
  const checkExistingSubscription = async () => {
    try {
      const result = await dispatch(getSubscriptionListAsync()).unwrap();
      if (result?.data && result.data.length > 0) {
        const activeSubscription = result.data.find(sub => sub.status === 'pending_payment' || sub.status === 'active');
        if (activeSubscription) {
          setLocalError(`You already have an active subscription (ID: ${activeSubscription.id}). Cancel it first to start a new one.`);
          setSelectedSubscriptionId(activeSubscription.id);
        }
      }
    } catch (err) {
      console.log('No existing subscriptions or error fetching:', err);
    }
  };

  // ========== CALENDAR FUNCTIONS ==========
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDay };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const getDateStr = (month, day) => {
    const year = month.getFullYear();
    const monthNum = String(month.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${monthNum}-${dayStr}`;
  };

  const getMealsForDate = (dateStr) => {
    return customBookingEntries.filter(entry => entry.date === dateStr);
  };

  const handleDateClick = (day) => {
    const dateStr = getDateStr(currentMonth, day);
    setSelectedDate(day);
    setPickerDate(dateStr);
    setShowMealPicker(true);
  };

  const handleAddMealFromPicker = () => {
    if (!pickerDate) return;
    
    const existingIndex = customBookingEntries.findIndex(
      entry => entry.date === pickerDate && entry.meal_time === pickerMealTime && entry.meal_type === pickerMealType
    );
    
    if (existingIndex !== -1) {
      alert(`You already have ${pickerMealTime} (${pickerMealType}) for this date.`);
      return;
    }
    
    const newEntry = {
      date: pickerDate,
      meal_time: pickerMealTime,
      meal_type: pickerMealType === 'veg' ? 'Veg' : 'Non-Veg',
      quantity: pickerQuantity
    };
    
    setCustomBookingEntries([...customBookingEntries, newEntry]);
    setShowMealPicker(false);
    setPickerMealTime('Breakfast');
    setPickerMealType('veg');
    setPickerQuantity(1);
  };

  const handleRemoveEntry = (index) => {
    setCustomBookingEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAllEntries = () => {
    if (customBookingEntries.length === 0) return;
    if (window.confirm('Are you sure you want to clear all entries?')) {
      setCustomBookingEntries([]);
    }
  };

  // ========== CUSTOM DATE ORDER ==========
  const handleCustomDateOrder = async () => {
    if (customBookingEntries.length === 0) {
      alert('Please add at least one meal entry before confirming.');
      return;
    }

    setLocalError(null);

    try {
      const groupedByDate = customBookingEntries.reduce((acc, entry) => {
        if (!acc[entry.date]) {
          acc[entry.date] = [];
        }
        acc[entry.date].push({
          meal_time: entry.meal_time,
          meal_type: entry.meal_type,
          quantity: entry.quantity
        });
        return acc;
      }, {});

      const bookings = Object.entries(groupedByDate).map(([date, items]) => ({
        date,
        items
      }));

      const payload = { bookings };

      console.log('📤 Sending custom date order:', payload);

      const response = await customDateApi.placeOneTimeOrder(payload);
      
      console.log('✅ Custom date order placed:', response);

      if (response.status) {
        const orderData = response.data || response;
        const orderId = orderData.order_id || orderData.id;
        
        const totalAmount = customBookingEntries.reduce((sum, entry) => {
          const price = mealPrices[entry.meal_time] || 60;
          return sum + (price * entry.quantity);
        }, 0);
        
        const savedOrders = JSON.parse(localStorage.getItem('customOrders') || '[]');
        const newOrder = {
          ...orderData,
          id: orderId,
          date: new Date().toISOString(),
          total: totalAmount,
          items: customBookingEntries,
          payment_status: orderData.payment_status || 'pending',
          order_status: orderData.order_status || 'confirmed',
          order_type: 'tiffin_date_wise'
        };
        savedOrders.unshift(newOrder);
        localStorage.setItem('customOrders', JSON.stringify(savedOrders));
        
        const confirmMessage = `🎉 Custom Date Order Confirmed!\n\n` +
          `📅 ${customBookingEntries.length} meal(s) booked\n` +
          `💰 Total Amount: ₹${totalAmount}\n` +
          `🧾 Order ID: ${orderId || 'N/A'}\n` +
          `📋 Order Number: ${orderData.order_number || 'N/A'}\n\n` +
          `📋 Order saved to My Orders!`;
        
        alert(confirmMessage);
        
        setCustomBookingEntries([]);
        setShowModal(false);
        navigate('/my-orders');
      } else {
        throw new Error(response.message || 'Order placement failed');
      }
    } catch (error) {
      console.error('❌ Custom date order failed:', error);
      let errorMsg = 'Order placement failed. Please try again.';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      setLocalError(errorMsg);
      alert(`❌ ${errorMsg}`);
    }
  };

  // ========== MEAL ITEM FUNCTIONS ==========
  
  // ✅ Update veg quantity
  const updateVegQuantity = (index, delta) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const newQty = Math.max(0, newItems[index].veg.quantity + delta);
      newItems[index].veg.quantity = newQty;
      newItems[index].veg.selected = newQty > 0;
      return newItems;
    });
  };

  // ✅ Update non-veg quantity
  const updateNonVegQuantity = (index, delta) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const newQty = Math.max(0, newItems[index].nonVeg.quantity + delta);
      newItems[index].nonVeg.quantity = newQty;
      newItems[index].nonVeg.selected = newQty > 0;
      return newItems;
    });
  };

  // ✅ Toggle veg selection
  const toggleVeg = (index) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const currentlySelected = newItems[index].veg.selected;
      newItems[index].veg.selected = !currentlySelected;
      if (!newItems[index].veg.selected) {
        newItems[index].veg.quantity = 0;
      } else if (newItems[index].veg.quantity === 0) {
        newItems[index].veg.quantity = 1;
      }
      return newItems;
    });
  };

  // ✅ Toggle non-veg selection
  const toggleNonVeg = (index) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const currentlySelected = newItems[index].nonVeg.selected;
      newItems[index].nonVeg.selected = !currentlySelected;
      if (!newItems[index].nonVeg.selected) {
        newItems[index].nonVeg.quantity = 0;
      } else if (newItems[index].nonVeg.quantity === 0) {
        newItems[index].nonVeg.quantity = 1;
      }
      return newItems;
    });
  };

  // ✅ Build subscription items - INCLUDE BOTH Veg AND Non-Veg
  const buildSubscriptionItems = () => {
    const items = [];
    
    mealItems.forEach(meal => {
      // ✅ Add Veg item if selected
      if (meal.veg.selected && meal.veg.quantity > 0) {
        items.push({
          meal_time: meal.meal_time,
          meal_type: 'Veg',
          quantity: meal.veg.quantity
        });
      }
      // ✅ Add Non-Veg item if selected
      if (meal.nonVeg.selected && meal.nonVeg.quantity > 0) {
        items.push({
          meal_time: meal.meal_time,
          meal_type: 'Non-Veg',
          quantity: meal.nonVeg.quantity
        });
      }
    });
    
    console.log('📦 Items for subscription (Veg + Non-Veg):', items);
    return items;
  };

  // ✅ Build bookings for all dates
  const buildBookings = () => {
    const start = new Date(subscriptionStartDate);
    const items = buildSubscriptionItems();
    const bookings = [];
    
    for (let i = 0; i < duration; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      bookings.push({
        date: dateStr,
        items: items.map(item => ({ ...item }))
      });
    }
    
    return bookings;
  };

  // ✅ Get total items count per day
  const getTotalItems = () => {
    let count = 0;
    mealItems.forEach(meal => {
      if (meal.veg.selected && meal.veg.quantity > 0) count += meal.veg.quantity;
      if (meal.nonVeg.selected && meal.nonVeg.quantity > 0) count += meal.nonVeg.quantity;
    });
    return count;
  };

  // ✅ Get daily price
  const getDailyPrice = () => {
    let total = 0;
    mealItems.forEach(meal => {
      const price = mealPrices[meal.meal_time] || 60;
      if (meal.veg.selected && meal.veg.quantity > 0) {
        total += price * meal.veg.quantity;
      }
      if (meal.nonVeg.selected && meal.nonVeg.quantity > 0) {
        total += price * meal.nonVeg.quantity;
      }
    });
    return total;
  };

  // ✅ Get total price
  const getTotalPrice = () => {
    return getDailyPrice() * duration;
  };

  // ========== MONTHLY SUBSCRIPTION ==========
  const handleCreateSubscription = async () => {
    setLocalError(null);
    
    try {
      const items = buildSubscriptionItems();
      
      if (items.length === 0) {
        setLocalError('Please select at least one meal');
        return;
      }

      if (!deliveryAddress.trim()) {
        setLocalError('Please enter delivery address');
        return;
      }

      // ✅ Build bookings for all dates
      const bookings = buildBookings();

      // ✅ CORRECT PAYLOAD with meal_type and bookings
      const payload = {
        start_date: subscriptionStartDate.toISOString().split('T')[0],
        delivery_address: deliveryAddress.trim(),
        items: items,
        bookings: bookings
      };
      
      console.log('🚀 MONTHLY SUBSCRIPTION PAYLOAD:', JSON.stringify(payload, null, 2));
      
      const result = await dispatch(createSubscriptionAsync(payload)).unwrap();
      
      console.log('✅ Subscription created:', result);
      
      if (result.status) {
        const data = result.data;
        
        const confirmMessage = `🎉 Monthly Subscription Created!\n\n` +
          `📅 Start Date: ${data.start_date || payload.start_date}\n` +
          `📅 End Date: ${data.end_date || 'N/A'}\n` +
          `💰 Total: ₹${data.projected_total || getTotalPrice()}\n` +
          `💳 Deposit (50%): ₹${data.deposit_amount || Math.round(getTotalPrice() * 0.5)}\n` +
          `📋 Status: ${data.status || 'Pending'}\n\n` +
          `📊 Items:\n${items.map(item => 
            `  • ${item.meal_time} (${item.meal_type}) × ${item.quantity}`
          ).join('\n')}\n\n` +
          `📅 Duration: ${duration} days\n` +
          `📦 Delivery Address: ${payload.delivery_address}`;
        
        alert(confirmMessage);
        setShowModal(false);
        navigate('/my-orders');
      }
    } catch (err) {
      console.error('❌ Subscription failed:', err);
      let errorMsg = 'Failed to create subscription';
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
        if (errorMsg.includes('already have an active or pending monthly subscription')) {
          errorMsg = '⚠️ You already have an active subscription. Please cancel it first.';
          await checkExistingSubscription();
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setLocalError(errorMsg);
      alert(`❌ ${errorMsg}`);
    }
  };

  // ========== CANCEL SUBSCRIPTION ==========
  const handleCancelSubscription = async () => {
    if (!selectedSubscriptionId) {
      alert('No subscription found to cancel.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to cancel subscription #${selectedSubscriptionId}?`)) {
      return;
    }
    
    try {
      const result = await dispatch(cancelSubscriptionAsync(selectedSubscriptionId)).unwrap();
      console.log('✅ Subscription cancelled:', result);
      alert('✅ Subscription cancelled successfully!');
      setLocalError(null);
      setSelectedSubscriptionId(null);
      await checkExistingSubscription();
    } catch (err) {
      console.error('❌ Cancel subscription failed:', err);
      alert('❌ Failed to cancel subscription. Please try again.');
    }
  };

  // ========== UI HELPERS ==========
  const getMealPrice = (mealTime) => {
    return mealPrices[mealTime] || 60;
  };

  const getTotalCustomPrice = () => {
    return customBookingEntries.reduce((sum, entry) => {
      const price = mealPrices[entry.meal_time] || 60;
      return sum + (price * entry.quantity);
    }, 0);
  };

  const handleBookNow = async () => {
    setViewMode('monthly');
    setShowModal(true);
    await fetchWeeklyMenu();
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getMealIcon = (mealTime) => {
    const icons = {
      'Breakfast': '🌅',
      'Lunch': '☀️',
      'Dinner': '🌙'
    };
    return icons[mealTime] || '🍽️';
  };

  const displayError = localError || subscriptionError;

  // ========== RENDER WEEKLY MENU IN MODAL ==========
  const renderWeeklyMenuInModal = () => {
    if (weeklyMenuLoading) {
      return (
        <div className="text-center py-4">
          <FaSpinner className="animate-spin text-2xl mx-auto" style={{ color: colors.primary }} />
          <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>Loading weekly menu...</p>
        </div>
      );
    }

    if (weeklyMenuError) {
      return (
        <div className="text-center py-4">
          <p className="text-xs text-red-500">{weeklyMenuError}</p>
          <button
            onClick={fetchWeeklyMenu}
            className="mt-2 px-3 py-1 rounded-lg text-xs text-white transition-all hover:scale-105"
            style={{ backgroundColor: colors.primary }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!weeklyMenu || weeklyMenu.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-xs" style={{ color: colors.textSecondary }}>No weekly menu items available</p>
        </div>
      );
    }

    const groupedByDay = {};
    weeklyMenu.forEach(item => {
      const day = item.day || item.Day || 'Unknown';
      if (!groupedByDay[day]) {
        groupedByDay[day] = [];
      }
      groupedByDay[day].push(item);
    });

    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const mealTypeOrder = ['Breakfast', 'Lunch', 'Dinner'];

    return (
      <div className="space-y-3 max-h-64 overflow-y-auto pr-1" style={{ 
        maxHeight: '260px',
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.primary} transparent`
      }}>
        {dayOrder.map(day => {
          if (!groupedByDay[day] || groupedByDay[day].length === 0) return null;
          
          const mealsByTime = {};
          groupedByDay[day].forEach(item => {
            const time = item.available_time || item.meal_time || 'Other';
            if (!mealsByTime[time]) {
              mealsByTime[time] = [];
            }
            mealsByTime[time].push(item);
          });

          return (
            <div key={day} className="rounded-lg p-2" style={{ 
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`
            }}>
              <div className="text-xs font-bold mb-1.5" style={{ color: colors.primary }}>
                {day}
              </div>
              <div className="space-y-1.5">
                {mealTypeOrder.map(mealType => {
                  if (!mealsByTime[mealType] || mealsByTime[mealType].length === 0) return null;
                  
                  return (
                    <div key={mealType} className="pl-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-medium" style={{ color: colors.textSecondary }}>
                          {getMealIcon(mealType)} {mealType}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {mealsByTime[mealType].map((item, idx) => {
                          const name = item.food_name || item.name || 'Menu Item';
                          const price = item.price || item.amount || 0;
                          const type = item.meal_type || item.type || 'Veg';
                          const isVeg = type === 'Veg' || type === 'veg';
                          
                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-1.5 rounded-lg cursor-pointer hover:shadow-sm transition-all"
                              style={{ 
                                backgroundColor: colors.card,
                                border: `1px solid ${colors.border}`
                              }}
                              onClick={() => {
                                alert(`🍽️ ${name}\n💰 ₹${price}\n🥗 ${type}\n📅 ${day}\n⏰ ${mealType}`);
                              }}
                            >
                              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                <span className="text-[10px]" style={{ 
                                  color: isVeg ? '#22C55E' : '#EF4444'
                                }}>
                                  {isVeg ? '🟢' : '🔴'}
                                </span>
                                <span className="text-[10px] font-medium truncate" style={{ color: colors.text }}>
                                  {name}
                                </span>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <span className="text-[10px] font-bold" style={{ color: colors.primary }}>
                                  ₹{price}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div className="text-center text-[10px] pt-1" style={{ color: colors.textSecondary }}>
          {weeklyMenu.length} items total
        </div>
      </div>
    );
  };

  // ========== RENDER CALENDAR ==========
  const renderCalendar = () => {
    const { daysInMonth, firstDay } = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleString('default', { month: 'long' });
    const year = currentMonth.getFullYear();

    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-12 h-12" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = getDateStr(currentMonth, day);
      const hasMeal = customBookingEntries.some(entry => entry.date === dateStr);
      const isToday = day === new Date().getDate() && 
                     currentMonth.getMonth() === new Date().getMonth() &&
                     currentMonth.getFullYear() === new Date().getFullYear();
      
      days.push(
        <button
          key={day}
          className={`w-12 h-12 rounded-xl text-sm font-medium transition-all hover:scale-105 relative ${
            hasMeal ? 'border-2' : ''
          } ${isToday ? 'ring-2 ring-offset-2' : ''}`}
          style={{
            backgroundColor: hasMeal ? colors.primary : 'transparent',
            color: hasMeal ? '#FFFFFF' : colors.text,
            borderColor: hasMeal ? colors.primary : colors.border,
            ...(isToday && { ringColor: colors.primary })
          }}
          onClick={() => handleDateClick(day)}
        >
          {day}
          {hasMeal && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white dark:border-gray-800" />
          )}
        </button>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold" style={{ color: colors.text }}>
            {monthName} {year}
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 rounded-full transition-all hover:scale-110"
              style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
            >
              <FaChevronLeft style={{ color: colors.text }} />
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 rounded-full transition-all hover:scale-110"
              style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
            >
              <FaChevronRight style={{ color: colors.text }} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium py-1" style={{ color: colors.textSecondary }}>
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>

        <div className="flex items-center gap-4 pt-2 border-t" style={{ borderColor: colors.border }}>
          <div className="flex items-center gap-1 text-xs" style={{ color: colors.textSecondary }}>
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.primary }} />
            Selected
          </div>
          <div className="flex items-center gap-1 text-xs" style={{ color: colors.textSecondary }}>
            <div className="w-3 h-3 rounded-full bg-green-500" />
            Has Meal
          </div>
        </div>
      </div>
    );
  };

  // ========== MEAL PICKER MODAL ==========
  const MealPickerModal = () => {
    if (!showMealPicker || !pickerDate) return null;

    const dateObj = new Date(pickerDate);
    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
    const dayNum = dateObj.getDate();

    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
        onClick={() => setShowMealPicker(false)}
      >
        <div
          className="rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold" style={{ color: colors.text }}>
                {dayName}, {dayNum} {monthName}
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                Select meal for this date
              </p>
            </div>
            <button 
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: colors.inputBackground }}
              onClick={() => setShowMealPicker(false)}
            >
              <FaTimes style={{ color: colors.text }} />
            </button>
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Select Meal Time
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['Breakfast', 'Lunch', 'Dinner'].map((time) => {
                const timeLabels = { 
                  'Breakfast': { icon: '🌅', label: 'Breakfast', time: '7:30-10 AM' },
                  'Lunch': { icon: '☀️', label: 'Lunch', time: '12-2:30 PM' },
                  'Dinner': { icon: '🌙', label: 'Dinner', time: '7:30-10 PM' }
                };
                const info = timeLabels[time];
                const isSelected = pickerMealTime === time;
                return (
                  <button
                    key={time}
                    className={`py-2.5 px-2 rounded-xl text-xs font-medium transition-all hover:scale-105 ${
                      isSelected ? 'border-2' : 'border'
                    }`}
                    style={{
                      borderColor: isSelected ? colors.primary : colors.border,
                      backgroundColor: isSelected ? colors.primary + '20' : colors.inputBackground,
                      color: isSelected ? colors.primary : colors.text
                    }}
                    onClick={() => setPickerMealTime(time)}
                  >
                    <div className="text-lg">{info.icon}</div>
                    <div>{info.label}</div>
                    <div className="text-[8px]" style={{ color: colors.textSecondary }}>{info.time}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Meal Type
            </label>
            <div className="flex gap-3">
              <button
                className={`flex-1 py-2.5 rounded-xl font-medium transition-all hover:scale-105 ${
                  pickerMealType === 'veg' ? 'border-2' : 'border'
                }`}
                style={{
                  borderColor: pickerMealType === 'veg' ? '#22C55E' : colors.border,
                  backgroundColor: pickerMealType === 'veg' ? '#22C55E20' : colors.inputBackground,
                  color: pickerMealType === 'veg' ? '#22C55E' : colors.text
                }}
                onClick={() => setPickerMealType('veg')}
              >
                <FaLeaf className="inline mr-1.5" /> Veg
              </button>
              <button
                className={`flex-1 py-2.5 rounded-xl font-medium transition-all hover:scale-105 ${
                  pickerMealType === 'nonveg' ? 'border-2' : 'border'
                }`}
                style={{
                  borderColor: pickerMealType === 'nonveg' ? '#EF4444' : colors.border,
                  backgroundColor: pickerMealType === 'nonveg' ? '#EF444420' : colors.inputBackground,
                  color: pickerMealType === 'nonveg' ? '#EF4444' : colors.text
                }}
                onClick={() => setPickerMealType('nonveg')}
              >
                <FaFish className="inline mr-1.5" /> Non-Veg
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Quantity
            </label>
            <div className="flex items-center gap-4">
              <button
                className="w-10 h-10 rounded-full border flex items-center justify-center transition-all hover:scale-105"
                style={{ borderColor: colors.border, backgroundColor: colors.inputBackground }}
                onClick={() => setPickerQuantity(Math.max(1, pickerQuantity - 1))}
              >
                <FaMinus className="text-xs" style={{ color: colors.text }} />
              </button>
              <span className="text-xl font-bold min-w-[30px] text-center" style={{ color: colors.text }}>
                {pickerQuantity}
              </span>
              <button
                className="w-10 h-10 rounded-full border flex items-center justify-center transition-all hover:scale-105"
                style={{ borderColor: colors.border, backgroundColor: colors.inputBackground }}
                onClick={() => setPickerQuantity(pickerQuantity + 1)}
              >
                <FaPlus className="text-xs" style={{ color: colors.text }} />
              </button>
            </div>
          </div>

          <button
            className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            style={{
              backgroundColor: colors.primary,
              boxShadow: `0 4px 20px ${colors.primary}40`
            }}
            onClick={handleAddMealFromPicker}
          >
            <FaPlus /> Add {pickerMealTime} for {formatDate(dateObj)}
          </button>

          {getMealsForDate(pickerDate).length > 0 && (
            <div className="mt-4">
              <label className="text-sm font-medium block mb-2" style={{ color: colors.textSecondary }}>
                Existing meals for this date
              </label>
              <div className="space-y-2">
                {getMealsForDate(pickerDate).map((entry, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ backgroundColor: colors.inputBackground }}
                  >
                    <span className="text-sm">
                      {getMealIcon(entry.meal_time)} {entry.meal_time} × {entry.quantity}
                    </span>
                    <span className="text-xs" style={{ color: colors.textSecondary }}>
                      {entry.meal_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== RENDER MEAL SELECTION ==========
  const renderMealSelection = () => {
    return mealItems.map((meal, index) => (
      <div 
        key={index} 
        className="rounded-xl p-4" 
        style={{ 
          backgroundColor: colors.inputBackground, 
          border: `1px solid ${colors.border}`
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xl">{getMealIcon(meal.meal_time)}</span>
          <div>
            <h4 className="font-bold" style={{ color: colors.text }}>{meal.meal_time}</h4>
            <p className="text-xs" style={{ color: colors.textSecondary }}>
              <FaClock className="inline mr-1 text-[10px]" /> {meal.time_slot}
            </p>
          </div>
          <span className="ml-auto text-xs px-2 py-1 rounded-full" style={{ 
            backgroundColor: colors.primary + '20',
            color: colors.primary
          }}>
            ₹{mealPrices[meal.meal_time]} / meal
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Veg */}
          <div 
            className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
              meal.veg.selected ? 'border-2 border-green-500' : 'border border-gray-300'
            }`}
            style={{ 
              backgroundColor: meal.veg.selected ? '#22C55E10' : colors.background,
            }}
            onClick={() => toggleVeg(index)}
          >
            <div className="flex items-center gap-2">
              <FaLeaf className="text-green-500" />
              <span className="text-sm font-medium" style={{ color: colors.text }}>Veg</span>
            </div>
            {meal.veg.selected ? (
              <div className="flex items-center gap-2">
                <button 
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: '#EF4444', color: 'white' }}
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    updateVegQuantity(index, -1); 
                  }}
                >
                  <FaMinus className="text-xs" />
                </button>
                <span className="font-bold min-w-[20px] text-center" style={{ color: colors.text }}>
                  {meal.veg.quantity}
                </span>
                <button 
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: '#22C55E', color: 'white' }}
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    updateVegQuantity(index, 1); 
                  }}
                >
                  <FaPlus className="text-xs" />
                </button>
              </div>
            ) : (
              <button 
                className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                style={{ backgroundColor: colors.border, color: colors.textSecondary }}
                type="button"
                onClick={(e) => { 
                  e.preventDefault();
                  e.stopPropagation(); 
                  toggleVeg(index); 
                }}
              >
                <FaPlus className="text-xs" />
              </button>
            )}
          </div>

          {/* Non-Veg */}
          <div 
            className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
              meal.nonVeg.selected ? 'border-2 border-red-500' : 'border border-gray-300'
            }`}
            style={{ 
              backgroundColor: meal.nonVeg.selected ? '#EF444410' : colors.background,
            }}
            onClick={() => toggleNonVeg(index)}
          >
            <div className="flex items-center gap-2">
              <FaFish className="text-red-500" />
              <span className="text-sm font-medium" style={{ color: colors.text }}>Non-Veg</span>
            </div>
            {meal.nonVeg.selected ? (
              <div className="flex items-center gap-2">
                <button 
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: '#EF4444', color: 'white' }}
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    updateNonVegQuantity(index, -1); 
                  }}
                >
                  <FaMinus className="text-xs" />
                </button>
                <span className="font-bold min-w-[20px] text-center" style={{ color: colors.text }}>
                  {meal.nonVeg.quantity}
                </span>
                <button 
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: '#22C55E', color: 'white' }}
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    updateNonVegQuantity(index, 1); 
                  }}
                >
                  <FaPlus className="text-xs" />
                </button>
              </div>
            ) : (
              <button 
                className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                style={{ backgroundColor: colors.border, color: colors.textSecondary }}
                type="button"
                onClick={(e) => { 
                  e.preventDefault();
                  e.stopPropagation(); 
                  toggleNonVeg(index); 
                }}
              >
                <FaPlus className="text-xs" />
              </button>
            )}
          </div>
        </div>
      </div>
    ));
  };

  // ========== MAIN RENDER ==========
  return (
    <div className="min-h-screen w-full pb-20" style={{ backgroundColor: colors.background }}>
      {/* Full Screen Hero */}
      <div className="fixed inset-0 w-full h-full flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1920&h=1080&fit=crop)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white w-full px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm border border-yellow-400/30 px-5 py-2 rounded-full text-sm font-medium text-yellow-200 mb-6">
            <FaFire className="text-yellow-400" /> Fresh & Homemade
          </div>
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-3 leading-tight">Tiffin Express</h1>
          <p className="text-2xl sm:text-3xl md:text-4xl text-yellow-300 font-semibold mb-4">Dil Se Plate Tak ❤️</p>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto mb-8">Ghar jaisa swaad, har dabbe ke saath. Fresh, homemade meals delivered daily.</p>

          <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-sm text-white/70">
            <span className="flex items-center gap-2"><FaClock className="text-yellow-400" /> Breakfast 7:30-10 AM</span>
            <span className="w-px h-4 bg-white/20 hidden sm:block"></span>
            <span className="flex items-center gap-2"><FaClock className="text-yellow-400" /> Lunch 12-2:30 PM</span>
            <span className="w-px h-4 bg-white/20 hidden sm:block"></span>
            <span className="flex items-center gap-2"><FaClock className="text-yellow-400" /> Dinner 7:30-10 PM</span>
          </div>

          <button 
            className="px-10 py-4 rounded-full text-white font-semibold text-lg shadow-2xl flex items-center gap-3 mx-auto transition-all hover:scale-105 active:scale-95" 
            style={{ background: 'linear-gradient(135deg, #FD8A36, #E07324)', boxShadow: '0 8px 40px rgba(253, 138, 54, 0.4)' }} 
            onClick={handleBookNow}
          >
            <FaCalendarCheck className="text-xl" /> Book Now <FaArrowRight className="text-sm" />
          </button>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-sm text-white/60">
            <span className="flex items-center gap-2"><FaUtensils className="text-yellow-400" /> 42+ Dishes</span>
            <span className="text-white/20">•</span>
            <span className="flex items-center gap-2"><FaLeaf className="text-green-400" /> Veg & Non-Veg</span>
            <span className="text-white/20">•</span>
            <span className="flex items-center gap-2"><FaTruck className="text-blue-400" /> Free Delivery</span>
          </div>
        </div>
      </div>

      {/* ========== BOOKING MODAL ========== */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setShowModal(false)}>
          <div className="rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold" style={{ color: colors.text }}>Tiffin Express</h3>
                <p className="text-sm" style={{ color: colors.textSecondary }}>Dil Se Plate Tak ❤️</p>
              </div>
              <button 
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110" 
                style={{ backgroundColor: colors.inputBackground }} 
                onClick={() => setShowModal(false)}
              >
                <FaTimes style={{ color: colors.text }} />
              </button>
            </div>

            {/* Weekly Menu Section */}
            <div className="mb-4 p-3 rounded-xl" style={{ backgroundColor: colors.primary + '10', border: `1px solid ${colors.primary}25` }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium flex items-center gap-2" style={{ color: colors.primary }}>
                  <FaCalendarWeek className="text-xs" /> Weekly Menu
                </p>
                <span className="text-[8px] px-2 py-0.5 rounded-full" style={{ 
                  backgroundColor: colors.primary + '20',
                  color: colors.primary
                }}>
                  {weeklyMenu.length} items
                </span>
              </div>
              {renderWeeklyMenuInModal()}
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2 mb-6 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              <button 
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${viewMode === 'monthly' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'}`} 
                style={{ color: viewMode === 'monthly' ? colors.primary : colors.textSecondary, boxShadow: viewMode === 'monthly' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' }} 
                onClick={() => setViewMode('monthly')}
              >
                <FaCalendarWeek className="text-xs" /> Monthly Subscription
              </button>
              <button 
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${viewMode === 'custom' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'}`} 
                style={{ color: viewMode === 'custom' ? colors.primary : colors.textSecondary, boxShadow: viewMode === 'custom' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' }} 
                onClick={() => setViewMode('custom')}
              >
                <FaCalendarDay className="text-xs" /> Custom Date
              </button>
            </div>

            {displayError && (
              <div className="p-3 rounded-xl mb-4 text-sm" style={{ backgroundColor: displayError.includes('active') ? '#FEF3C7' : '#FEF2F2', color: displayError.includes('active') ? '#92400E' : '#DC2626', border: `1px solid ${displayError.includes('active') ? '#FCD34D' : '#FECACA'}` }}>
                <div className="flex items-start gap-2">
                  <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                  <div>
                    <span>{displayError}</span>
                    {selectedSubscriptionId && (
                      <button onClick={handleCancelSubscription} className="block mt-2 px-4 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:scale-105" style={{ backgroundColor: '#EF4444' }}>
                        <FaTrash className="inline mr-1.5 text-[10px]" /> Cancel Existing Subscription
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button onClick={checkExistingSubscription} className="w-full mb-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2" style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
              <FaList className="text-xs" /> Check My Subscriptions
            </button>

            {/* ========== MONTHLY SUBSCRIPTION VIEW ========== */}
            {viewMode === 'monthly' && !displayError?.includes('active') && !selectedSubscriptionId && (
              <div className="space-y-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: colors.primary + '10', border: `1px solid ${colors.primary}25` }}>
                  <p className="text-sm font-medium flex items-center gap-2" style={{ color: colors.primary }}>
                    <FaInfoCircle className="text-xs" /> Monthly Setup Plan
                  </p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>Configure your regular daily default meals and selected start date.</p>
                </div>

                {/* Duration Selection */}
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    📆 Duration (Days)
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {durationOptions.map((days) => (
                      <button
                        key={days}
                        className={`py-2 rounded-xl text-sm font-medium transition-all hover:scale-105 ${
                          duration === days ? 'border-2' : 'border'
                        }`}
                        style={{
                          borderColor: duration === days ? colors.primary : colors.border,
                          backgroundColor: duration === days ? colors.primary + '20' : colors.inputBackground,
                          color: duration === days ? colors.primary : colors.text
                        }}
                        onClick={() => setDuration(days)}
                      >
                        {days} Days
                      </button>
                    ))}
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>📅 Start Date</label>
                  <div className="flex items-center justify-between p-3 rounded-xl cursor-pointer hover:shadow-md transition-all" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.border}` }} onClick={() => setShowCalendar(!showCalendar)}>
                    <span className="text-sm font-medium" style={{ color: colors.text }}>{formatDate(subscriptionStartDate)}</span>
                    <FaCalendarAlt style={{ color: colors.primary }} />
                  </div>
                  {showCalendar && (
                    <div className="mt-2 p-3 rounded-xl" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                      <Calendar onChange={(date) => { setSubscriptionStartDate(date); setShowCalendar(false); }} value={subscriptionStartDate} minDate={new Date()} className="w-full" />
                    </div>
                  )}
                </div>

                {/* Delivery Address */}
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    📍 Delivery Address <span className="text-xs" style={{ color: colors.textSecondary }}>(Required)</span>
                  </label>
                  <input
                    type="text"
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Enter your delivery address"
                    className="w-full p-3 rounded-xl focus:outline-none"
                    style={{ 
                      backgroundColor: colors.inputBackground, 
                      border: `1px solid ${colors.border}`, 
                      color: colors.text 
                    }}
                  />
                </div>

                {/* Meal Selection */}
                <div>
                  <label className="text-sm font-medium block mb-2" style={{ color: colors.textSecondary }}>
                    Select Meals for All {duration} Days
                  </label>
                  {renderMealSelection()}
                </div>

                {/* Order Summary */}
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.primary + '10', border: `1px solid ${colors.primary}30` }}>
                  <h4 className="font-semibold flex items-center gap-2" style={{ color: colors.text }}>
                    <FaInfoCircle className="text-xs" /> Order Summary
                  </h4>
                  <div className="flex justify-between text-sm mt-2">
                    <span style={{ color: colors.textSecondary }}>
                      {getTotalItems()} meal(s) per day • {duration} days
                    </span>
                    <span className="font-bold text-lg" style={{ color: colors.primary }}>
                      ₹{getTotalPrice().toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs mt-1" style={{ color: colors.textSecondary }}>
                    <span>Daily Total: ₹{getDailyPrice()}</span>
                    <span>Deposit (50%): ₹{Math.round(getTotalPrice() * 0.5).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs" style={{ color: colors.textSecondary }}>
                    <span>Total Meals: {getTotalItems() * duration}</span>
                    <span>Outstanding: ₹{Math.round(getTotalPrice() * 0.5).toLocaleString()}</span>
                  </div>
                </div>

                {/* Confirm Button */}
                <button 
                  className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50" 
                  style={{ 
                    backgroundColor: colors.primary, 
                    boxShadow: `0 4px 20px ${colors.primary}40`,
                    opacity: getTotalItems() > 0 && deliveryAddress.trim() ? 1 : 0.5,
                    cursor: getTotalItems() > 0 && deliveryAddress.trim() ? 'pointer' : 'not-allowed'
                  }} 
                  onClick={handleCreateSubscription}
                  disabled={isSubscribing || getTotalItems() === 0 || !deliveryAddress.trim()}
                >
                  {isSubscribing ? (
                    <><FaSpinner className="animate-spin" /> Creating Subscription...</>
                  ) : (
                    <><FaCalendarAlt className="inline mr-2" /> Confirm {duration} Days Subscription</>
                  )}
                </button>

                {getTotalItems() === 0 && (
                  <p className="text-center text-xs mt-2" style={{ color: colors.textSecondary }}>
                    Please select at least one meal (Veg or Non-Veg)
                  </p>
                )}
                {deliveryAddress.trim() === '' && getTotalItems() > 0 && (
                  <p className="text-center text-xs mt-2" style={{ color: colors.textSecondary }}>
                    Please enter delivery address
                  </p>
                )}
              </div>
            )}

            {displayError?.includes('active') && selectedSubscriptionId && (
              <div className="text-center py-6">
                <div className="text-5xl mb-4">⚠️</div>
                <h4 className="text-lg font-bold mb-2" style={{ color: colors.text }}>Active Subscription Found</h4>
                <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>You already have an active subscription (ID: {selectedSubscriptionId}). Please cancel it first to create a new one.</p>
                <button onClick={handleCancelSubscription} className="px-6 py-2.5 rounded-xl text-white font-medium transition-all hover:scale-105 flex items-center justify-center gap-2 mx-auto" style={{ backgroundColor: '#EF4444' }}>
                  <FaTrash className="text-sm" /> Cancel Subscription
                </button>
              </div>
            )}

            {/* ========== CUSTOM DATE VIEW ========== */}
            {viewMode === 'custom' && (
              <div className="space-y-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: colors.primary + '10', border: `1px solid ${colors.primary}25` }}>
                  <p className="text-sm font-medium flex items-center gap-2" style={{ color: colors.primary }}>
                    <FaInfoCircle className="text-xs" /> Custom Date Order
                  </p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>Select dates and meals for one-time delivery.</p>
                </div>

                {/* Calendar */}
                <div className="rounded-xl p-4" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                  {renderCalendar()}
                </div>

                {/* Selected Entries Summary */}
                {customBookingEntries.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                        Selected Meals ({customBookingEntries.length})
                      </label>
                      <button
                        className="text-xs font-medium transition-all hover:scale-105 flex items-center gap-1"
                        style={{ color: '#EF4444' }}
                        onClick={handleClearAllEntries}
                      >
                        <FaEraser className="text-[10px]" /> Clear All
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customBookingEntries.map((entry, index) => {
                        const mealColors = { 'Breakfast': '#F59E0B', 'Lunch': '#F97316', 'Dinner': '#6366F1' };
                        const price = mealPrices[entry.meal_time] || 60;
                        return (
                          <div key={index} className="flex items-center justify-between p-2.5 rounded-lg" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.border}` }}>
                            <div className="text-xs">
                              <span className="font-semibold" style={{ color: colors.text }}>{formatDate(new Date(entry.date))}</span>
                              <span className="ml-2" style={{ color: mealColors[entry.meal_time] }}>{getMealIcon(entry.meal_time)} {entry.meal_time}</span>
                              <span className="ml-2" style={{ color: colors.primary }}>×{entry.quantity}</span>
                              <span className="ml-2 text-[8px]" style={{ color: colors.textSecondary }}>₹{price * entry.quantity}</span>
                            </div>
                            <button
                              className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                              style={{ backgroundColor: '#EF444420', color: '#EF4444' }}
                              onClick={() => handleRemoveEntry(index)}
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      <div className="text-sm font-bold" style={{ color: colors.primary }}>
                        Total: ₹{getTotalCustomPrice()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Confirm Button */}
                <button
                  className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: customBookingEntries.length > 0 ? colors.primary : colors.border,
                    boxShadow: customBookingEntries.length > 0 ? `0 4px 20px ${colors.primary}40` : 'none',
                    opacity: customBookingEntries.length > 0 ? 1 : 0.5,
                    cursor: customBookingEntries.length > 0 ? 'pointer' : 'not-allowed'
                  }}
                  onClick={handleCustomDateOrder}
                  disabled={isLoading || customBookingEntries.length === 0}
                >
                  {isLoading ? (
                    <><FaSpinner className="animate-spin" /> Processing...</>
                  ) : (
                    <><FaCalendarCheck /> Confirm Custom Order</>
                  )}
                </button>
              </div>
            )}

            <p className="text-xs text-center mt-4" style={{ color: colors.textSecondary + '80' }}>By booking, you agree to our Terms of Service</p>
          </div>
        </div>
      )}

      {/* Meal Picker Modal */}
      <MealPickerModal />
    </div>
  );
};

export default TiffinService; 